import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import { useNotifications } from '../../../notifications';
import { deleteChecklistTemplate } from '../../../api/checklist-queries';
import styles from './modals.module.scss';

interface DeleteChecklistTemplateModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  template: {
    id: string;
    name: string;
  } | null;
  refetch: () => void;
}

export const DeleteChecklistTemplateModal: React.FC<DeleteChecklistTemplateModalProps> = ({
  show,
  setShow,
  template,
  refetch,
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [deleteTemplate, { loading: deleting }] = useMutation(deleteChecklistTemplate);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!template) return;

    setIsSubmitting(true);

    try {
      const result = await deleteTemplate({
        variables: { id: template.id },
      });

      if (result.data?.deleteChecklistTemplate?.errors?.length) {
        notifyError({
          message: {
            id: 'checklist-template.delete-error',
            defaultMessage: result.data.deleteChecklistTemplate.errors[0] || 'Failed to delete template',
          },
        });
        setIsSubmitting(false);
        return;
      }

      notifySuccess({
        message: {
          id: 'checklist-template.deleted',
          defaultMessage: 'Template deleted successfully',
        },
      });

      await refetch();
      setShow(false);
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist-template.delete-error',
          defaultMessage: 'An error occurred while deleting the template',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <PropdocsModal
      size="sm"
      show={show}
      setShow={() => setShow(false)}
      title={<FormattedMessage id="checklist-template.delete-title" defaultMessage="Delete Template" />}
    >
      <>
        <div className={styles.modalBody}>
          <p>
            <FormattedMessage id="checklist-template.delete-confirm" defaultMessage="Are you sure you want to delete this template?" />
          </p>
          <p>
            <strong>{template.name}</strong>
          </p>
          <p className={styles.warningText}>
            <FormattedMessage id="checklist-template.delete-warning" defaultMessage="This action cannot be undone." />
          </p>
        </div>

        <div className={styles.modalActions}>
          <Button variant="link" onClick={() => setShow(false)} disabled={isSubmitting}>
            <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? <FormattedMessage id="cta.deleting" defaultMessage="Deleting..." /> : <FormattedMessage id="cta.delete" defaultMessage="Delete" />}
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default DeleteChecklistTemplateModal;
